import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
} from '@mui/material';
import cancelOrderReasons from 'common/constants/cancelOrderReasons';
import { fillLaterCancelWarning } from 'common/constants/fillLater';
import MarkdownField from 'common/forms/MarkdownField';
import SelectField from 'common/forms/SelectField';
import { validateMaxLength } from 'common/forms/formValidations';
import useInfoDialog from 'common/hooks/useInfoDialog';
import useRoles from 'common/hooks/useRoles';
import { keys, map } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch } from 'react-redux';
import { cancelOrder } from 'store/thunks/patientThunks';
import { filterTransactions } from 'store/thunks/transactionsThunk';

const CancelOrderButton = ({
  orderId,
  mpi,
  transactionId,
  disabled,
  hasShipment,
  fillLaterCount,
  onSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { InfoDialog, showInfoDialog } = useInfoDialog();
  const dispatch = useDispatch();
  const hasAccess = useRoles();

  const fillLaterWarning = fillLaterCancelWarning(fillLaterCount);

  const handleCloseModal = () => setShowModal(false);
  const handleOpenModal = () => setShowModal(true);

  const handleCancelOrder = ({ cancelReason, note }) => {
    showInfoDialog({
      title: 'Confirm Cancel',
      message: `Are you sure you want to cancel this order, ${orderId}?${hasShipment ? ' This will also delete the associated shipment.' : ''}${fillLaterWarning ? ` ${fillLaterWarning}` : ''}`,
      callback: () => {
        setIsLoading(true);
        dispatch(cancelOrder({ mpi, orderId, cancelReason, note })).then((isCanceled) => {
          setIsLoading(false);
          if (isCanceled) {
            enqueueSnackbar(`Order ${orderId} Canceled`, { variant: 'warning' });
            setShowModal(false);
            onSuccess?.();
          }

          /* This is for cancelling the order from the transaction list */
          if (transactionId) {
            dispatch(filterTransactions({ filter: { transactionType: 'FAILED' } }));
          }
        });
      },
      cancelable: true,
      isWarning: true,
      confirmButtonText: 'Yes',
    });
  };

  return (
    <>
      <InfoDialog />

      <Dialog
        open={showModal}
        onClose={handleCloseModal}
        aria-describedby='CancelOrder-Dialog-header'
        fullWidth
      >
        <DialogTitle id='CancelOrder-header'>{`Cancel Order ${orderId}`}</DialogTitle>

        <Form
          onSubmit={handleCancelOrder}
          render={({ handleSubmit, invalid }) => (
            <form noValidate onSubmit={handleSubmit}>
              <DialogContent>
                <SelectField
                  id='CancelOrder-cancelReason-dropdown'
                  label='Reason for canceling order'
                  name='cancelReason'
                  required
                >
                  {map(keys(cancelOrderReasons), (key) => (
                    <MenuItem value={key} key={key}>
                      {cancelOrderReasons[key]}
                    </MenuItem>
                  ))}
                </SelectField>

                <MarkdownField
                  name='note'
                  id='CancelOrder-cancelNote-input'
                  validations={[
                    validateMaxLength('The note is too long. Please make it shorter', 65435),
                  ]}
                  label='Cancel Note'
                  required
                />

                {hasShipment && (
                  <Alert
                    severity='warning'
                    sx={{
                      mt: 1,
                      bgcolor: '#fff3e0',
                      color: '#ff9800',
                      '& .MuiAlert-icon': { color: '#ff9800' },
                    }}
                  >
                    This order has an active shipment. Cancelling it will delete the associated
                    shipment.
                  </Alert>
                )}

                {fillLaterWarning && (
                  <Alert
                    severity='warning'
                    sx={{
                      mt: 1,
                      bgcolor: '#fff3e0',
                      color: '#ff9800',
                      '& .MuiAlert-icon': { color: '#ff9800' },
                    }}
                  >
                    {fillLaterWarning}
                  </Alert>
                )}
              </DialogContent>

              <DialogActions>
                <Button variant='outlined' color='secondary' onClick={handleCloseModal}>
                  Cancel
                </Button>
                <LoadingButton
                  loading={isLoading}
                  variant='contained'
                  type='submit'
                  disabled={invalid}
                >
                  Submit
                </LoadingButton>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>

      <Grid>
        <LoadingButton
          variant='outlined'
          color='error'
          onClick={handleOpenModal}
          disabled={!hasAccess.cancelOrder || disabled}
          sx={{
            px: 1,
            fontSize: '0.75rem',
            minHeight: '30px',
            lineHeight: 'normal',
          }}
        >
          Cancel
        </LoadingButton>
      </Grid>
    </>
  );
};

CancelOrderButton.propTypes = {
  orderId: PropTypes.string.isRequired,
  mpi: PropTypes.string.isRequired,
  transactionId: PropTypes.string,
  disabled: PropTypes.bool,
  hasShipment: PropTypes.bool,
  fillLaterCount: PropTypes.number,
  onSuccess: PropTypes.func,
};

CancelOrderButton.defaultProps = {
  transactionId: undefined,
  disabled: false,
  hasShipment: false,
  fillLaterCount: 0,
  onSuccess: undefined,
};

export default CancelOrderButton;
